import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'antd';
import { Document, pdf } from '@react-pdf/renderer';

import { convertBlobToBase64, openPrinterWithUrl } from '@/utils/file';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { setAppLoader } from '../../../../../redux/common/CommonActions';
import { downloadBlobFile } from '../../../../../utilz';
import { routeFileSelector } from '../../../../../utilz/i18n';
import Button from '../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import CommonModal from '../../../../components/Modals/CommonModal';
import ConfirmationModal from '../../../../components/Modals/ConfirmationModal';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import TMSFiledCard from '../../../common/components/TMSFiledCard';
import LADClaimPDF from '../../../Print/Claims/LossAndDamage/LADClaimPDF';
import { PrintFooter } from '../../common/PrintFooter';
import { useGetLADPrintForm } from '../../hooks';
import { useCheckXpoExport, useGetClaimDetails, useSendEmailCarrier, useSendEmailXpo } from '../hooks';
import LADPrintClaim from '../Prints/LADPrintClaim';
import LDSendToCarrierForm from '../Prints/LDSendToCarrierForm';

// First attachment row (per default file list sort) gets a fresh client-rendered claim PDF when checked; other checked rows use `fileContent` from the API.
const LAD_EMAIL_CLAIM_PRINT_ATTACHMENT_INDEX = 0;

const LADDrawerHeader = ({ claimData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [printModal, setPrintModal] = useState(false);
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [isXpoExport, setIsXpoExport] = useState(false);
  const [exportForm] = Form.useForm();
  const { pathname: activeTab, state: locationState } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const selectedClaimKey = useSelector((state) => state.lossAndDamage.selectedClaimKey);
  const { data: claimDetails } = useGetClaimDetails(selectedClaimKey);
  const { mutate: checkXpoExport, isPending: isCheckingXpoExport } = useCheckXpoExport();
  const { mutate: sendEmailCarrier } = useSendEmailCarrier();
  const { mutate: sendEmailXpo } = useSendEmailXpo();

  const {
    data: claimDetailsBatch,
    refetch: refetchClaimDetailsBatch,
    isLoading: isClaimDetailsBatchLoading,
  } = useGetLADPrintForm({
    ClaimKeys: String(claimData?.claimKey || ''),
    enabled: false,
  });

  const handleGeneratePDF = async () => {
    try {
      dispatch(setAppLoader(true));
      const fileName = 'Claim Details.pdf';
      const blob = await pdf(<PDFdocument claimData={claimDetailsBatch?.[0]} />).toBlob();
      downloadBlobFile(blob, fileName);
      dispatch(setAppLoader(false));
    } catch (error) {
      console.log(error);
      dispatch(setAppLoader(false));
    }
  };

  const handlePrintForm = () => {
    refetchClaimDetailsBatch();
    setPrintModal(true);
  };

  const handleSendEmailToCarrier = async (payload) => {
    const handleDone = () => {
      setSendEmailModal(false);
      setIsXpoExport(false);
    };

    try {
      dispatch(setAppLoader(true));
      const attachments = (payload.attachments ?? []).map((attachment) => ({ ...attachment }));

      const claimPrintSlot = attachments[LAD_EMAIL_CLAIM_PRINT_ATTACHMENT_INDEX];
      if (claimPrintSlot?.isAttached) {
        const { data } = await refetchClaimDetailsBatch();
        const claimDataForPdf = data?.[0] ?? claimDetailsBatch?.[0];

        if (claimDataForPdf) {
          const blob = await pdf(<PDFdocument claimData={claimDataForPdf} />).toBlob();
          const fileBase64 = await convertBlobToBase64(blob);
          attachments[LAD_EMAIL_CLAIM_PRINT_ATTACHMENT_INDEX] = {
            ...claimPrintSlot,
            fileContent: String(fileBase64).split(',')[1],
          };
        }
      }

      const finalPayload = {
        ...payload,
        attachments,
        handleDone,
      };

      if (isXpoExport) {
        sendEmailXpo(finalPayload, { onSuccess: handleDone });
      } else {
        sendEmailCarrier(finalPayload, { onSuccess: handleDone });
      }
    } catch (error) {
      console.log(error);
      dispatch(setAppLoader(false));
    }
  };

  const handleXpoExportClick = () => {
    if (!claimDetails?.claimKey) {
      return;
    }
    checkXpoExport(
      { ClaimKey: claimDetails.claimKey },
      {
        onSuccess: () => {
          setSendEmailModal(true);
          setIsXpoExport(true);
        },
      },
    );
  };

  const shouldShowEmailPrompt =
    Number(locationState?.showEmailPromptForClaimKey) > 0 &&
    Number(locationState?.showEmailPromptForClaimKey) === Number(claimDetails?.claimKey);

  const handleDefaultEmailSendDialogueCancel = () => {
    navigate(activeTab, { replace: true });
  };

  const handlePrint = async () => {
    let pdfUrl = '';
    try {
      const blob = await pdf(<PDFdocument claimData={claimDetailsBatch?.[0]} />).toBlob();
      pdfUrl = URL.createObjectURL(blob);
      await openPrinterWithUrl(pdfUrl, 'claims-pdf-print-iframe');
    } catch {
      showNotification({
        message: t('translation:default.fields.printError'),
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    }
  };

  return (
    <Fragment>
      <Row justify="space-between" className="align-items-center" gutter={30}>
        <Col>
          <Row gutter={30}>
            <Col>
              <TMSFiledCard labelClass="text-light f-16" title="Claim Number">
                <PrimaryLabel normal className="f-20 text-white">
                  {claimData?.claimNumber}
                </PrimaryLabel>
              </TMSFiledCard>
            </Col>
            <Col>
              <TMSFiledCard labelClass="text-light f-16" title="Currency">
                <PrimaryLabel normal className="f-20 text-white">
                  {claimData?.currencyCode}
                </PrimaryLabel>
              </TMSFiledCard>
            </Col>
          </Row>
        </Col>
        <Col className="pr-30">
          {claimDetails?.claimKey ? (
            <Row gutter={15}>
              <Col>
                <Button type="default" invertMode onClick={handlePrintForm}>
                  Print Form
                </Button>
              </Col>
              <Col>
                <Button type="default" invertMode onClick={() => setSendEmailModal(true)}>
                  Email To Transportation Provider
                </Button>
              </Col>
              <Col>
                <Button type="default" invertMode onClick={handleXpoExportClick} loading={isCheckingXpoExport}>
                  XPO Export
                </Button>
              </Col>
            </Row>
          ) : null}
        </Col>
      </Row>
      <CommonModal
        className="vw-80"
        open={printModal}
        title="Loss And Damage Print"
        okText="Print"
        onClose={() => setPrintModal(false)}
        customFooter={<PrintFooter handlePrint={handlePrint} generatePDF={handleGeneratePDF} setOpen={setPrintModal} />}
        afterOpenChange={(val) => {
          if (!val) {
            exportForm.resetFields();
          }
        }}
      >
        <div className="h-100 custom-scroll-bar">
          {isClaimDetailsBatchLoading ? (
            <ContentSpinner size="large" />
          ) : (
            <LADPrintClaim claimData={claimDetailsBatch ? claimDetailsBatch?.[0] : {}} />
          )}
        </div>
      </CommonModal>
      <CommonModal
        className="vh-65 custom-claim-email-send-popup"
        open={sendEmailModal}
        title="Email To Transportation Provider"
        // biome-ignore lint/complexity/noUselessFragments: placeholder
        customFooter={<></>}
        onClose={() => setSendEmailModal(false)}
        destroyOnHidden
      >
        <LDSendToCarrierForm
          handleSendEmailToCarrier={handleSendEmailToCarrier}
          setSendEmailModal={setSendEmailModal}
        />
      </CommonModal>
      <ConfirmationModal
        className="w-auto"
        title="Alert"
        open={shouldShowEmailPrompt}
        description={
          <>
            {t('translation:claims.successFullyCreated')}. {t('translation:default.fields.claimNumber')} :{' '}
            {claimDetails?.claimNumber} <br />
            {t('translation:claims.sendEmailToTransportationProviderConfirmation')}
          </>
        }
        onOk={() => {
          setSendEmailModal(true);
          handleDefaultEmailSendDialogueCancel();
        }}
        onClose={handleDefaultEmailSendDialogueCancel}
        onCancel={handleDefaultEmailSendDialogueCancel}
      />
    </Fragment>
  );
};

export default LADDrawerHeader;

const PDFdocument = ({ claimData }) => {
  return (
    <Document>
      <LADClaimPDF claimDetails={claimData} />
    </Document>
  );
};
